type FieldErrors = Record<string, string[]>

// Everything the error envelope itself owns. What is left over after removing
// these is a field name — `handleGetResponseData` spreads `details` up into
// `data`, so a rejection like `{password: [...]}` arrives flattened alongside
// them rather than nested under `details.field_errors`.
const ENVELOPE_KEYS = new Set([
  "message",
  "detail",
  "type",
  "status_code",
  "description",
  "data",
  "details",
  "code",
  "actions",
  "missing_fields",
  "field_errors"
])

/** Field-shaped entries: a name mapped to a message or list of messages. */
function collectFieldErrors(source: unknown): FieldErrors | null {
  if (!source || typeof source !== "object") return null

  const found: FieldErrors = {}

  for (const [name, value] of Object.entries(source)) {
    if (ENVELOPE_KEYS.has(name)) continue

    const messages = Array.isArray(value) ? value : [value]
    if (messages.length && messages.every((m) => typeof m === "string")) {
      found[name] = messages as string[]
    }
  }

  return Object.keys(found).length ? found : null
}

/**
 * The part of an API error a form should render.
 *
 * Field errors win: a form can put those messages on the fields they belong to,
 * which is where the user is looking. Anything with no field to land on
 * degrades to the message string, which the form shows in its shared banner.
 *
 * Both envelope shapes are accepted. The auth path runs through
 * `handleGetResponseData`, which flattens `details` into `data`; other callers
 * pass the raw envelope with `details.field_errors` intact.
 */
export function pickFormError(error: unknown): FieldErrors | string | null {
  if (!error) return null
  if (typeof error === "string") return error

  const envelope = error as {
    message?: unknown
    field_errors?: unknown
    details?: { field_errors?: unknown }
  }

  const named =
    collectFieldErrors(envelope.field_errors) ??
    collectFieldErrors(envelope.details?.field_errors) ??
    collectFieldErrors(envelope) ??
    collectFieldErrors(envelope.details)

  if (named) return named

  const { message } = envelope

  if (typeof message === "string") return message

  if (message && typeof message === "object") {
    const { detail } = message as { detail?: unknown }
    if (typeof detail === "string") return detail
  }

  return null
}
